import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { History, X, Mic, Play } from "lucide-react";
import MobileHeader from "components/mobile-header";
import useCreateNotes from "zustand-store/create-notes";
import PageHeader from "components/page-header";
import { Helmet } from 'react-helmet-async';
import SaveSession from "sections/create-notes/save-session";
import SessionHistoryPanel from "sections/create-notes/session-history-panel";
import SelectAudioDevice from "sections/audio-check/select-audio-device";
import MicTest from "sections/audio-check/mic-test";
import { useGlobalAudioRecorder } from "zustand-store/global-recorder";
import FeedbackForm from "components/feedback-form";

const CreateNotes = () => {
  const { restoreSession, sessionStatus, isGeneratingNotes } = useCreateNotes();
  const { isRecording } = useGlobalAudioRecorder();

  const location = useLocation();
  const [showWelcome, setShowWelcome] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [sessionKey, setSessionKey] = useState(0);

  useEffect(() => {
    if (location.state?.showWelcome) {
      setTimeout(() => setShowWelcome(true), 2000);
    }
    if (location.state?.showDemo) {
      setTimeout(() => setShowDemo(true), 2000);
    }
  }, [location.state]);

  const toggleHistory = () => {
    setShowHistory(prev => {
      if (!prev) setHistoryKey(k => k + 1);
      return !prev;
    });
  };

  const handleContinueSession = async ({ sessionId, patientName, duration, transcribedText, sessionNotes }) => {
    const generatedNotes = (sessionNotes || []).map(n => ({
      templateId: parseInt(n.templateId),
      output: n.notesContent,
    }));
    const selectedTemplates = (sessionNotes || []).map(n => parseInt(n.templateId));

    try { localStorage.removeItem('nirvascribe_active_session'); } catch (_) {}

    restoreSession({ sessionId, patientName, timerTick: duration, transcribedText, generatedNotes, selectedTemplates });
    setShowHistory(false);
    setSessionKey(k => k + 1);
  };

  return (
    <>
      <div className="w-full px-4 md:px-6 lg:px-6">
        <Helmet>
          <title>NirvaScribe</title>
          <meta name="description" content="NirvaScribe - AI note-taking tool for Healthcare Professionals." />
          <link rel="canonical" href="https://app.nirvascribe.com/create-notes" />
        </Helmet>

        <MobileHeader />

        <PageHeader label="Create Notes">
          <button
            onClick={toggleHistory}
            disabled={isRecording || isGeneratingNotes}
            title={
              isRecording ? "Stop recording before viewing sessions"
              : isGeneratingNotes ? "Notes are being generated"
              : showHistory ? "Back to Create Notes"
              : "View session history"
            }
            className={`flex items-center gap-2 px-3 py-[6px] rounded-lg text-sm font-semibold border transition-all duration-200 active:scale-95 ${
              isRecording || isGeneratingNotes
                ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed opacity-60'
                : showHistory
                  ? 'bg-gray-900 text-white border-gray-900 hover:bg-black'
                  : 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-gray-300 hover:from-pink-600 hover:to-purple-700'
            }`}
          >
            {showHistory ? (
              <><X className="h-4 w-4" /><span className="hidden sm:inline">Close</span></>
            ) : (
              <><History className="h-4 w-4" /><span className="hidden sm:inline">My Sessions</span></>
            )}
          </button>
        </PageHeader>

        {/* Audio Setup Welcome Modal */}
        {showWelcome && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl animate-popup flex flex-col max-h-[92vh] sm:max-h-[88vh]">

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Mic size={16} strokeWidth={2} className="text-gray-800" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      Audio Setup
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Quick check before your first session
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowWelcome(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Close"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 px-6 py-5">

                {/* Section 1 — Devices */}
                <div className="mb-2">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-5 h-5 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                      1
                    </span>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Select your audio devices
                    </h3>
                  </div>
                  <SelectAudioDevice />
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 mb-5" />

                {/* Section 2 — Mic test */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-5 h-5 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                      2
                    </span>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Test your microphone
                    </h3>
                  </div>
                  <MicTest />
                </div>

              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
                <button
                  onClick={() => setShowWelcome(false)}
                  className="text-sm font-semibold px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-700 transition-colors"
                >
                  Done, let's go
                </button>
              </div>

            </div>
          </div>
        )}

        {/* Quick demo popup — shown 2s after onboarding completes */}
        {showDemo && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl animate-popup flex flex-col max-h-[92vh] sm:max-h-[88vh]">

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Play size={14} strokeWidth={2} className="text-gray-800 ml-0.5" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      See how it works
                    </h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      A quick walkthrough of NirvaScribe
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDemo(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors shrink-0"
                  aria-label="Skip"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              </div>

              {/* Video */}
              <div className="flex-1 overflow-hidden">
                {process.env.REACT_APP_ONBOARDING_VIDEO_URL ? (
                  <iframe
                    src={process.env.REACT_APP_ONBOARDING_VIDEO_URL}
                    title="NirvaScribe walkthrough"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full min-h-[240px] border-0"
                  />
                ) : (
                  <div className="flex items-center justify-center h-48 bg-gray-50 text-gray-400 text-sm">
                    Video not configured
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
                <button
                  onClick={() => setShowDemo(false)}
                  className="text-sm font-semibold px-6 py-2.5 text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Skip for now
                </button>
              </div>

            </div>
          </div>
        )}

        {/* Floating Feedback button — always visible, bottom-right */}
        <div className="fixed bottom-6 right-6 z-40">
          <FeedbackForm variant="floating" label="Feedback" />
        </div>

        {/* Main content */}
        {showHistory ? (
          <div key={`history-${historyKey}`} className="animate-fade-up">
            <SessionHistoryPanel
              onContinueSession={handleContinueSession}
              isRecording={isRecording}
              hasActiveSession={sessionStatus === 'in_progress'}
            />
          </div>
        ) : (
          <div key={`session-${sessionKey}`} className="animate-fade-up">
            <SaveSession />
          </div>
        )}

      </div>
    </>
  );
};

export default CreateNotes;
